/**
 * QuickAwayDialog - Simplified departure dialog for quick "Mark Away"
 *
 * Minimal fields: optional expected return date + optional notes.
 * Uses useCreateDepartureEvent() to create a departed event.
 */

import { useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { useCreateDepartureEvent } from '@/hooks/useAllocationEvents'

interface QuickAwayDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tailNumber: string
  allocationId: string
  aircraftId: string
  locationId: string
}

export function QuickAwayDialog({
  open,
  onOpenChange,
  tailNumber,
  allocationId,
  aircraftId,
  locationId,
}: QuickAwayDialogProps) {
  const [expectedReturn, setExpectedReturn] = useState('')
  const [notes, setNotes] = useState('')
  const departureEvent = useCreateDepartureEvent()

  const resetForm = () => {
    setExpectedReturn('')
    setNotes('')
  }

  const handleMarkAway = async () => {
    try {
      await departureEvent.mutateAsync({
        allocation_id: allocationId,
        aircraft_id: aircraftId,
        location_id: locationId,
        expected_return_at: expectedReturn ? new Date(expectedReturn).toISOString() : null,
        notes: notes || null,
      })
      toast.success(`${tailNumber} marked as away`)
      resetForm()
      onOpenChange(false)
    } catch (err) {
      toast.error('Failed to mark as away')
      console.error('Quick away error:', err)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next) resetForm(); onOpenChange(next) }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark {tailNumber} Away</AlertDialogTitle>
          <AlertDialogDescription>
            Record a temporary departure. The aircraft will show as &quot;Away&quot; on the map.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="qa-return" className="text-xs">Expected Return</Label>
            <Input
              id="qa-return"
              type="datetime-local"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value)}
              className="h-8 text-sm"
            />
            <p className="text-[10px] text-muted-foreground">
              Leave blank if return time is unknown
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="qa-notes" className="text-xs">Notes</Label>
            <Textarea
              id="qa-notes"
              placeholder="Optional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="text-sm min-h-[60px]"
              rows={2}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={resetForm}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleMarkAway}
            disabled={departureEvent.isPending}
          >
            {departureEvent.isPending ? 'Marking...' : 'Mark Away'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
